import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import styles from './KnowledgeAdmin.module.css';
import { departments } from '../config/departments';
import Tooltip from '../components/Tooltip';
import { HelpCircle } from 'lucide-react';

const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';

interface Article {
  id: number;
  slug: string;
  title: string;
  content: string;
  category: string;
  skillLevel?: string;
  status: string | number; // Can be enum number or string
  tags?: string[];
  viewCount?: number;
  createdAt?: string;
  updatedAt?: string;
  isPublicForVisitors?: boolean;
  isPublicForProspects?: boolean;
  isRequiredForProspects?: boolean;
  isRequiredForMembers?: boolean;
  isRequiredForLeaders?: boolean;
  requiredForPositionIds?: number[];
  requiredForDepartments?: string[];
  isContextualHelp?: boolean;
  contextualHelpFor?: string;
  cardImageUrl?: string;
}

const KnowledgeAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const isEditMode = !!id;

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    content: '',
    category: 'flight',
    skillLevel: 'Beginner',
    tags: [] as string[],
    isPublicForVisitors: false,
    isPublicForProspects: false,
    isRequiredForProspects: false,
    isRequiredForMembers: false,
    isRequiredForLeaders: false,
    requiredForPositionIds: [] as number[],
    requiredForDepartments: [] as string[],
    isContextualHelp: false,
    contextualHelpFor: '',
    cardImageUrl: '',
  });
  const [cardImageFile, setCardImageFile] = useState<File | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentView, setCurrentView] = useState<'list' | 'editor'>('list');
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);

  const categories = [
    'flight',
    'mining',
    'salvage',
    'cargo',
    'ground-ops',
    'medical',
    'sop',
    'training',
  ];

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced'];

  // Helper to convert status enum to string
  const getStatusString = (status: string | number): string => {
    if (typeof status === 'string') return status;
    const statusMap: { [key: number]: string } = {
      0: 'Draft',
      1: 'Published',
      2: 'Deprecated'
    };
    return statusMap[status] || 'Draft';
  };

  useEffect(() => {
    // Check if user has permission (Tier 4 or Admin)
    const canEdit = user?.positionTier === 4 || user?.role === 'Admin';
    if (!canEdit) {
      navigate('/knowledge');
      return;
    }

    loadAvailableTags();

    if (id && isEditMode) {
      loadArticleForEdit(parseInt(id));
    } else {
      loadArticles();
    }
  }, [id]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('knowledgebase/admin/articles');
      setArticles(response.data);
    } catch (err) {
      console.error('Error loading articles:', err);
      alert('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTags = async () => {
    try {
      const response = await axios.get<string[]>('knowledgebase/tags');
      setAvailableTags(response.data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const loadArticleForEdit = async (articleId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(`knowledgebase/admin/articles/${articleId}`);
      const article = response.data;
      setFormData({
        slug: article.slug,
        title: article.title,
        content: article.content,
        category: article.category,
        skillLevel: article.skillLevel || 'Beginner',
        tags: article.tags || [],
        isPublicForVisitors: article.isPublicForVisitors || false,
        isPublicForProspects: article.isPublicForProspects || false,
        isRequiredForProspects: article.isRequiredForProspects || false,
        isRequiredForMembers: article.isRequiredForMembers || false,
        isRequiredForLeaders: article.isRequiredForLeaders || false,
        requiredForPositionIds: article.requiredForPositionIds || [],
        requiredForDepartments: article.requiredForDepartments || [],
        isContextualHelp: article.isContextualHelp || false,
        contextualHelpFor: article.contextualHelpFor || '',
        cardImageUrl: article.cardImageUrl || '',
      });
      setSelectedArticleId(article.id);
      setCurrentView('editor');
    } catch (err) {
      console.error('Error loading article:', err);
      alert('Failed to load article');
      navigate('/knowledge/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setFormData({ ...formData, slug });
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
      setShowSuggestions(false);
    }
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);

    if (value.trim().length > 0) {
      const suggestions = availableTags.filter(
        (tag) =>
          tag.toLowerCase().includes(value.toLowerCase()) &&
          !formData.tags.includes(tag)
      );
      setTagSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (tag: string) => {
    setFormData({ ...formData, tags: [...formData.tags, tag] });
    setTagInput('');
    setShowSuggestions(false);
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });
  };

  const handleCardImageUpload = async () => {
    if (!cardImageFile) return;
    
    const formData = new FormData();
    formData.append('Image', cardImageFile);

    try {
      const res = await axios.post('/knowledgebase/upload-article-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Image uploaded:', res.data.imageUrl);
      setFormData(prev => ({ ...prev, cardImageUrl: res.data.imageUrl }));
      setCardImageFile(null);
      alert('Image uploaded successfully!');
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Failed to upload image');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.slug || !formData.title || !formData.content || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }

    console.log('📝 Submitting KB article with data:', {
      requiredForDepartments: formData.requiredForDepartments,
      isContextualHelp: formData.isContextualHelp,
      contextualHelpFor: formData.contextualHelpFor
    });

    try {
      // Use selectedArticleId to determine if editing, not isEditMode from URL
      if (selectedArticleId) {
        await axios.put(`knowledgebase/articles/${selectedArticleId}`, formData);
        alert('Article updated successfully!');
      } else {
        const response = await axios.post('knowledgebase/articles', formData);
        alert('Article created successfully!');
        setSelectedArticleId(response.data.id);
      }
      setCurrentView('list');
      loadArticles();
    } catch (err: any) {
      console.error('Error saving article:', err);
      alert(err.response?.data?.message || 'Failed to save article');
    }
  };

  const handlePublish = async (articleId: number) => {
    if (!window.confirm('Publish this article? It will be visible to all members.')) return;

    try {
      await axios.put(`knowledgebase/articles/${articleId}/publish`);
      alert('Article published!');
      loadArticles();
    } catch (err) {
      console.error('Error publishing article:', err);
      alert('Failed to publish article');
    }
  };

  const handleUnpublish = async (articleId: number) => {
    if (!window.confirm('Unpublish this article? It will be hidden from members.')) return;

    try {
      await axios.put(`knowledgebase/articles/${articleId}/unpublish`);
      alert('Article unpublished!');
      loadArticles();
    } catch (err) {
      console.error('Error unpublishing article:', err);
      alert('Failed to unpublish article');
    }
  };

  const handleDelete = async (articleId: number) => {
    if (!window.confirm('Delete this article permanently? This cannot be undone.')) return;

    try {
      await axios.delete(`knowledgebase/articles/${articleId}`);
      alert('Article deleted!');
      loadArticles();
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Failed to delete article');
    }
  };

  const startNewArticle = () => {
    setFormData({
      slug: '',
      title: '',
      content: '',
      category: 'flight',
      skillLevel: 'Beginner',
      tags: [],
      isPublicForVisitors: false,
      isPublicForProspects: false,
      isRequiredForProspects: false,
      isRequiredForMembers: false,
      isRequiredForLeaders: false,
      requiredForPositionIds: [],
      requiredForDepartments: [],
      isContextualHelp: false,
      contextualHelpFor: '',
      cardImageUrl: '',
    });
    setSelectedArticleId(null);
    setCurrentView('editor');
    navigate('/knowledge/admin'); // Clear any ID from URL
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (currentView === 'editor') {
    return (
      <div className={styles.adminContainer}>
        <div className={styles.header}>
          <h1>{isEditMode && selectedArticleId ? 'Edit Article' : 'Create New Article'}</h1>
          <button onClick={() => setCurrentView('list')} className={styles.backButton}>
            ← Back to List
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Slug (URL-friendly) *</label>
            <div className={styles.slugRow}>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                className={styles.input}
                placeholder="basic-flight-controls"
              />
              <button type="button" onClick={generateSlug} className={styles.generateButton}>
                Generate from Title
              </button>
            </div>
            <small className={styles.hint}>URL will be: /knowledge/{formData.slug}</small>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className={styles.select}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Skill Level</label>
              <select
                name="skillLevel"
                value={formData.skillLevel}
                onChange={handleInputChange}
                className={styles.select}
              >
                {skillLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Tags</label>
            <div className={styles.tagsInputWrapper}>
              <div className={styles.tagsInput}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => tagInput.trim().length > 0 && setShowSuggestions(tagSuggestions.length > 0)}
                  placeholder="Type to search existing tags or add new..."
                  className={styles.input}
                />
                <button type="button" onClick={addTag} className={styles.addTagButton}>
                  Add Tag
                </button>
              </div>
              {showSuggestions && tagSuggestions.length > 0 && (
                <div className={styles.tagSuggestions}>
                  {tagSuggestions.slice(0, 8).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className={styles.tagSuggestion}
                      onMouseDown={() => selectSuggestion(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.tags}>
              {formData.tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className={styles.removeTag}>
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isPublicForVisitors"
                checked={formData.isPublicForVisitors}
                onChange={(e) => setFormData({ ...formData, isPublicForVisitors: e.target.checked })}
                className={styles.checkbox}
              />
              <span>Make article visible to all visitors (non-registered users)</span>
            </label>
            <small className={styles.hint}>
              Check this to make the article publicly accessible to anyone, including non-registered visitors browsing the website.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isPublicForProspects"
                checked={formData.isPublicForProspects}
                onChange={(e) => setFormData({ ...formData, isPublicForProspects: e.target.checked })}
                className={styles.checkbox}
              />
              <span>Make article visible to Prospects (registered non-members)</span>
            </label>
            <small className={styles.hint}>
              Check this to make the article accessible to registered users who are not yet organization members. By default, only organization members can view articles.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isRequiredForProspects"
                checked={formData.isRequiredForProspects}
                onChange={(e) => setFormData({ ...formData, isRequiredForProspects: e.target.checked })}
                className={styles.checkbox}
              />
              <span>📚 Required for Prospects (Pre-Member Onboarding)</span>
            </label>
            <small className={styles.hint}>
              Check this to make the article required reading for new users before they become organization members. Appears in their onboarding checklist.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isRequiredForMembers"
                checked={formData.isRequiredForMembers}
                onChange={(e) => setFormData({ ...formData, isRequiredForMembers: e.target.checked })}
                className={styles.checkbox}
              />
              <span>📚 Required for Members (Post-Approval Onboarding)</span>
            </label>
            <small className={styles.hint}>
              Check this to make the article required reading for new organization members. Appears in their "Getting Started" onboarding checklist.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isRequiredForLeaders"
                checked={formData.isRequiredForLeaders}
                onChange={(e) => setFormData({ ...formData, isRequiredForLeaders: e.target.checked })}
                className={styles.checkbox}
              />
              <span>📚 Required for Leaders (Leadership Training)</span>
            </label>
            <small className={styles.hint}>
              Check this to make the article required reading for leaders and team leadership. Includes leadership-specific training materials.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Required for Specific Departments (Optional)
              <Tooltip content="Members of selected departments will see this article in their required reading list. Multiple departments can be selected." position="right" maxWidth="300px">
                <HelpCircle size={16} style={{ opacity: 0.6, cursor: 'help' }} />
              </Tooltip>
            </label>
            <div className={styles.checkboxGrid}>
              {departments.map(dept => (
                <label key={dept.id} className={styles.checkboxOption}>
                  <input
                    type="checkbox"
                    checked={formData.requiredForDepartments.includes(dept.id)}
                    onChange={(e) => {
                      const newDepts = e.target.checked
                        ? [...formData.requiredForDepartments, dept.id]
                        : formData.requiredForDepartments.filter(d => d !== dept.id);
                      console.log(`📋 Department ${dept.name} (${dept.id}) ${e.target.checked ? 'checked' : 'unchecked'}. New array:`, newDepts);
                      setFormData({ ...formData, requiredForDepartments: newDepts });
                    }}
                    className={styles.checkbox}
                  />
                  <span>{dept.name}</span>
                </label>
              ))}
            </div>
            <small className={styles.hint}>
              Select departments that must read this article. Members of these departments will see this in their required reading list.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isContextualHelp"
                checked={formData.isContextualHelp}
                onChange={(e) => setFormData({ ...formData, isContextualHelp: e.target.checked })}
                className={styles.checkbox}
              />
              <span>❓ Enable as Contextual Help</span>
              <Tooltip content="Creates a ? button on the selected page that shows a list of related help articles. Multiple articles can share the same feature area." position="right" maxWidth="300px">
                <HelpCircle size={16} style={{ opacity: 0.6, cursor: 'help', marginLeft: '0.5rem' }} />
              </Tooltip>
            </label>
            <small className={styles.hint}>
              Check this to show a help button (?) in specific feature areas that opens this article.
            </small>
          </div>

          {formData.isContextualHelp && (
            <div className={styles.formGroup}>
              <label>Feature Area *</label>
              <select
                value={formData.contextualHelpFor}
                onChange={(e) => setFormData({ ...formData, contextualHelpFor: e.target.value })}
                className={styles.select}
                required={formData.isContextualHelp}
              >
                <option value="">Select feature area...</option>
                <option value="medical-sos">Medical SOS</option>
                <option value="missions">Missions</option>
                <option value="events">Events</option>
                <option value="departments">Departments</option>
                <option value="positions">Positions</option>
                <option value="knowledge-base">Knowledge Base</option>
                <option value="feedback">Feedback System</option>
                <option value="org-points">Organization Points</option>
              </select>
              <small className={styles.hint}>
                Select which feature area this help article applies to.
              </small>
            </div>
          )}

          <div className={styles.formGroup}>
            <label>Card Banner Image</label>
            {formData.cardImageUrl && (
              <div className={styles.imagePreview}>
                <img src={formData.cardImageUrl} alt="Card preview" style={{ maxWidth: '300px', borderRadius: '8px' }} />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, cardImageUrl: '' })}
                  className={styles.removeImageButton}
                >
                  Remove Image
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCardImageFile(e.target.files?.[0] || null)}
              className={styles.input}
            />
            <button
              type="button"
              onClick={handleCardImageUpload}
              disabled={!cardImageFile}
              className={styles.uploadButton}
            >
              Upload Image
            </button>
            <small className={styles.hint}>
              Optional banner image displayed at the top of the article card (like department cards).
            </small>
          </div>

          <div className={styles.formGroup}>
            <label>Content *</label>
            <Editor
              apiKey={tinymceApiKey}
              value={formData.content}
              onEditorChange={(content) => setFormData({ ...formData, content })}
              init={{
                height: 500,
                menubar: true,
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                  'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                  'insertdatetime', 'media', 'table', 'help', 'wordcount'
                ],
                toolbar:
                  'undo redo | formatselect | bold italic underline forecolor backcolor | ' +
                  'alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | link image media | ' +
                  'removeformat | code | help',
                content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px }',
                branding: false,
              }}
            />
            <small className={styles.hint}>
              Rich text editor with formatting, images, links, and more.
            </small>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveButton}>
              {isEditMode && selectedArticleId ? 'Update Article' : 'Create Article'}
            </button>
            <button type="button" onClick={() => setCurrentView('list')} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className={styles.adminContainer}>
      <div className={styles.header}>
        <h1>Knowledge Base Management</h1>
        <button onClick={startNewArticle} className={styles.createButton}>
          + Create New Article
        </button>
      </div>

      {articles.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No articles yet. Create your first article!</p>
        </div>
      ) : (
        <div className={styles.articlesList}>
          {articles.map((article) => (
            <div key={article.id} className={styles.articleItem}>
              <div className={styles.articleInfo}>
                <h3>{article.title}</h3>
                <div className={styles.articleMeta}>
                  <span className={styles.category}>{article.category}</span>
                  <span className={styles.skillLevel}>{article.skillLevel}</span>
                  <span className={`${styles.status} ${styles[getStatusString(article.status).toLowerCase()]}`}>
                    {getStatusString(article.status)}
                  </span>
                </div>
                <p className={styles.slug}>/knowledge/{article.slug}</p>
              </div>

              <div className={styles.articleActions}>
                <button onClick={() => loadArticleForEdit(article.id)} className={styles.editButton}>
                  Edit
                </button>
                {getStatusString(article.status) === 'Draft' ? (
                  <button onClick={() => handlePublish(article.id)} className={styles.publishButton}>
                    Publish
                  </button>
                ) : (
                  <button onClick={() => handleUnpublish(article.id)} className={styles.unpublishButton}>
                    Unpublish
                  </button>
                )}
                <button onClick={() => handleDelete(article.id)} className={styles.deleteButton}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeAdmin;
